/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { EuiFlexItem, EuiIcon, EuiSkeletonText, EuiText } from '@elastic/eui'

import useGetSaasProviders from '@modules/billing-lib/queries/useGetSaasProviders'
import { FormattedMessage } from '@modules/i18n'

import type { FC, ReactNode } from 'react'

const KNOWN_CSP_NAMES = ['aws', 'gcp', 'azure'] as const
type KnownCSPName = (typeof KNOWN_CSP_NAMES)[number]

export const ZoneDisplayNameFlexItem: FC<{ zoneId: string | undefined }> = ({ zoneId }) => {
  const {
    data: providers,
    isLoading: isLoadingProviders,
    isError: isErrorProviders,
  } = useGetSaasProviders({
    enabled: zoneId !== undefined,
  })

  if (zoneId === undefined) {
    return null
  }

  if (isLoadingProviders) {
    return <EuiSkeletonText lines={1} />
  }

  if (isErrorProviders) {
    return (
      <EuiFlexItem>
        <EuiText color='subdued' size='s'>
          <EuiIcon type='alert' />
          <FormattedMessage id='ccm.cluster-services.zone.error' defaultMessage='Error' />
        </EuiText>
      </EuiFlexItem>
    )
  }

  const foundProvider = providers?.find((provider) =>
    provider.regions.some((region) => region.identifier === zoneId),
  )

  const foundRegion = foundProvider?.regions.find((region) => region.identifier === zoneId)

  if (
    foundRegion === undefined ||
    foundProvider === undefined ||
    !isKnownCSPName(foundProvider.name)
  ) {
    return null
  }

  return (
    <EuiFlexItem>
      <EuiText color='subdued' size='s'>
        {getProviderDisplayName(foundProvider.name)} - {foundRegion.name}
      </EuiText>
    </EuiFlexItem>
  )
}

function isKnownCSPName(regionName: string): regionName is KnownCSPName {
  return KNOWN_CSP_NAMES.includes(regionName as any)
}

function getProviderDisplayName(providerId: string): ReactNode {
  switch (providerId) {
    case 'aws':
      return 'AWS'
    case 'gcp':
      return 'GCP'
    case 'azure':
      return 'Azure'
    default:
      return 'Unknown Provider'
  }
}
